import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useCreateCustomer } from "@/lib/customers";
import { useCustomerCustomFields } from "@/lib/customer-custom-fields";
import { CustomFieldInput } from "./CustomFieldInput";
import { useToast } from "./Toast";

/**
 * Standalone "Add client" dialog. Renders the basic profile fields
 * (name, email, phone, notes) plus any tenant-defined customer custom
 * fields that aren't scoped — scoped fields can't be applied at create
 * time because the customer has no tags yet, and we can't pick
 * client-specific fields against a not-yet-created client.
 */
export function AddClientDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated?: (customerId: string) => void;
}) {
  const create = useCreateCustomer();
  const customFields = useCustomerCustomFields();
  const toast = useToast();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [fieldDraft, setFieldDraft] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  // Only tenant-wide fields apply at create time. Tag-scoped + client-
  // scoped fields require state that doesn't exist yet (no tags assigned,
  // no client id), so we exclude them from this surface — operators can
  // populate those after creation by reopening the client and editing.
  const applicableFields =
    customFields.data?.fields.filter(
      (f) =>
        (!f.scopeTagIds || f.scopeTagIds.length === 0) &&
        (!f.scopeClientIds || f.scopeClientIds.length === 0),
    ) ?? [];

  const reset = () => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setPhone("");
    setNotes("");
    setFieldDraft({});
    setError(null);
  };

  const onSubmit = async () => {
    setError(null);
    if (!firstName.trim() && !email.trim() && !phone.trim()) {
      setError("Enter at least a name, email, or phone.");
      return;
    }
    const customFieldValues = applicableFields
      .map((f) => ({
        fieldId: f.id,
        value: fieldDraft[f.id]?.trim() || null,
      }))
      .filter((row) => row.value !== null);

    try {
      const created = await create.mutateAsync({
        firstName: firstName.trim() || undefined,
        lastName: lastName.trim() || undefined,
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        notes: notes.trim() || undefined,
        customFieldValues: customFieldValues.length
          ? customFieldValues
          : undefined,
      });
      toast.success("Client added");
      reset();
      onClose();
      onCreated?.(created.id);
    } catch (e) {
      setError((e as Error).message || "Couldn't add client.");
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          reset();
          onClose();
        }
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add client</DialogTitle>
          <DialogDescription>
            Create a client without going through the booking flow. You can
            add tags and tag-scoped custom fields after creation.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3">
          <div className="grid gap-3 md:grid-cols-2">
            <div className="grid gap-1.5">
              <Label htmlFor="ac-first">First name</Label>
              <Input
                id="ac-first"
                autoComplete="given-name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="ac-last">Last name</Label>
              <Input
                id="ac-last"
                autoComplete="family-name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="ac-email">Email</Label>
            <Input
              id="ac-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="ac-phone">Phone</Label>
            <Input
              id="ac-phone"
              autoComplete="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="ac-notes">Notes</Label>
            <textarea
              id="ac-notes"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              placeholder="Allergies, preferences, anything operators should know."
            />
          </div>

          {applicableFields.length > 0 && (
            <div className="border-t border-border pt-3 space-y-3">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Custom fields
              </p>
              {applicableFields.map((f) => (
                <div key={f.id} className="grid gap-1.5">
                  <Label htmlFor={`cf-${f.id}`}>
                    {f.label}
                    {f.required && <span className="text-destructive"> *</span>}
                  </Label>
                  <CustomFieldInput
                    field={{ type: f.type, options: f.options, required: f.required }}
                    value={fieldDraft[f.id] ?? ""}
                    onChange={(v) =>
                      setFieldDraft((prev) => ({ ...prev, [f.id]: v }))
                    }
                  />
                </div>
              ))}
            </div>
          )}

          {error && (
            <p className="text-xs text-destructive bg-destructive/5 border border-destructive/20 rounded-md px-3 py-2">
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onSubmit} disabled={create.isPending}>
            {create.isPending ? "Adding…" : "Add client"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
